// ─────────────────────────────────────────────────────────────────────────────
// ServicesPage.jsx — /services
// ─────────────────────────────────────────────────────────────────────────────

import { useRef, useState, useEffect } from 'react'
import { Link }                         from 'react-router-dom'
import { services }                     from '@data/services'
import PageHero                         from '@components/common/PageHero'
import CtaBanner                        from '@components/common/CtaBanner'
import SEOHead                          from '@components/common/SEOHead'

// Gradient fallbacks per card
const BG = [
  'linear-gradient(135deg, #2d5c3e 0%, #3d7a55 100%)',
  'linear-gradient(135deg, #1e4d2f 0%, #2d6040 100%)',
  'linear-gradient(135deg, #163d25 0%, #255038 100%)',
  'linear-gradient(135deg, #0f3020 0%, #1e4d2f 100%)',
]

// Service icons
const ICONS = {
  consultation: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  lab: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"/>
    </svg>
  ),
  training: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
    </svg>
  ),
  support: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.5 10.5 19.79 19.79 0 011.21 2.18 2 2 0 013.22 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
    </svg>
  ),
}

// useFadeIn hook
function useFadeIn() {
  const ref = useRef(null)
  const [vis, setVis] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true) },
      { threshold: 0.1 }
    )
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [])
  return [ref, vis]
}

// Service Card — MIAVIT style: image with name overlay + chevron, description, button
function ServiceCard({ service, index, triggered }) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:       'flex',
        flexDirection: 'column',
        opacity:       triggered ? 1 : 0,
        transform:     triggered ? 'translateY(0)' : 'translateY(20px)',
        transition:    `opacity 0.45s ease ${index * 0.1}s, transform 0.45s ease ${index * 0.1}s`,
      }}
    >
      {/* Image with name overlay — MIAVIT exact style */}
      <div style={{
        position:    'relative',
        aspectRatio: '25/14',
        overflow:    'hidden',
        background:  service.image ? 'transparent' : BG[index % BG.length],
        flexShrink:  0,
      }}>
        {service.image ? (
          <img src={service.image} alt={service.name}
            style={{
              width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              transform:  hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.4s ease',
            }}
          />
        ) : (
          // Icon placeholder when no image
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'rgba(255,255,255,0.3)',
          }}>
            {ICONS[service.icon]}
          </div>
        )}

        {/* Name strip with > chevron — MIAVIT exact */}
        <Link
          to={`/services/${service.slug}`}
          style={{
            position:       'absolute', bottom: 0, left: 0, right: 0,
            display:        'flex', alignItems: 'center', justifyContent: 'space-between',
            padding:        '10px 14px',
            background:     hovered ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.58)',
            fontFamily:     "'DM Sans', sans-serif",
            fontSize:       '13.5px', fontWeight: 500,
            color:          '#ffffff', textDecoration: 'none',
            transition:     'background 0.2s ease',
          }}
        >
          <span>{service.shortName}</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>
      </div>

      {/* Content */}
      <div style={{ paddingTop: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <p style={{
          fontFamily:      "'DM Sans', sans-serif",
          fontSize:        '13.5px', color: '#55544e',
          lineHeight:      1.65, margin: '0 0 16px', flex: 1,
          display:         '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow:        'hidden',
        }}>
          {service.description}
        </p>

        {/* Delivery methods tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '16px' }}>
          {service.deliveryMethods.map(m => (
            <span key={m} style={{
              fontFamily:    "'DM Sans', sans-serif",
              fontSize:      '10.5px', fontWeight: 600,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              color:         '#007935', padding: '2px 8px',
              background:    'rgba(0,121,53,0.07)',
            }}>
              {m}
            </span>
          ))}
        </div>

        {/* Read more — MIAVIT style */}
        <Link
          to={`/services/${service.slug}`}
          style={{
            display:        'inline-flex', alignItems: 'center',
            fontFamily:     "'DM Sans', sans-serif",
            fontSize:       '13px', fontWeight: 600,
            color:          hovered ? '#ffffff' : '#007935',
            textDecoration: 'none',
            padding:        '7px 16px',
            border:         '1.5px solid #007935',
            background:     hovered ? '#007935' : 'transparent',
            transition:     'all 0.2s ease',
            alignSelf:      'flex-start',
          }}
        >
          Read more ...
        </Link>
      </div>
    </div>
  )
}

// Main page
export default function ServicesPage() {
  const [ref, vis] = useFadeIn()

  return (
    <main>
      <SEOHead
        title="Services"
        description="Salient Agrinutrition offers on-farm technical consultation, lab testing, training programmes, and after-sales support for livestock producers across Sri Lanka."
        path="/services"
      />

      {/* PageHero */}
      <PageHero
        title="Services"
        subtitle="Beyond product distribution - we deliver expert guidance, training, and responsive technical support to help your operation thrive."
        breadcrumbs={[
          { label: 'Home',     to: '/' },
          { label: 'Services' },
        ]}
      />

      {/* Services section */}
      <section style={{ background: '#ffffff', padding: '64px 0 72px' }}>
        <div style={{
          maxWidth: 'var(--container-max)', margin: '0 auto',
          padding:  '0 var(--container-padding)',
        }}>

          {/* Intro heading + subtitle — MIAVIT style 
          <h2 style={{
            fontFamily:    "'DM Sans', sans-serif",
            fontSize:      'clamp(1.5rem, 3vw, 2.5rem)',
            fontWeight:    300, letterSpacing: '0.02em',
            color:         '#007935', margin: '0 0 12px',
          }}>
            Services
          </h2>
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize:   '14.5px', color: '#55544e',
            lineHeight: 1.75, margin: '0 0 48px', maxWidth: '680px',
          }}>
            At Salient Agrinutrition we offer the most adapted solutions to our customers.
            We provide a series of personalised services — from on-farm technical consultation
            and laboratory testing to training programmes and responsive after-sales support.
          </p> */}

          {/* 4-col service cards grid */}
          <div
            ref={ref}
            className="services-grid"
            style={{
              display:             'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap:                 '32px',
              alignItems:          'start',
            }}
          >
            {services.map((s, i) => (
              <ServiceCard key={s.id} service={s} index={i} triggered={vis} />
            ))}
          </div>

        </div>
      </section>

      {/* CtaBanner */}
      <CtaBanner
        tag="Get in Touch"
        title="Let's talk about your needs!"
        body="At Salient we not only distribute premium nutritional solutions - we also offer a range of expert services and technical know-how. Interested?"
        buttonText="Contact us"
        buttonTo="/contact"
      />

      <style>{`
        @media (max-width: 767px) { .services-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </main>
  )
}